// Where the comparison page's return table goes (Drew, 2026-11-03). The design puts it
// beside the chart, and it stays there whatever the number of strategies: when the
// columns need more room than the column has, arrows shift it one column at a time.

/** Room each money column needs: "$1,384,200" in the mono face with its badge under it. */
export const MONEY_COL = 100;
export const LABEL_COL = 140;
const COL_GAP = 12;
const TABLE_PAD = 32;
/** One column over: what each arrow click moves the table. */
export const COL_STEP = MONEY_COL + COL_GAP;
/** Where the money columns start: the left padding, the label column and its gap. */
export const MONEY_START = TABLE_PAD / 2 + LABEL_COL + COL_GAP;
/** The narrowest the table can be before its columns crowd; past this the arrows show. */
export function returnTableWidth(columns: number): number {
  return LABEL_COL + columns * MONEY_COL + columns * COL_GAP + TABLE_PAD;
}

/**
 * "side": the design's layout, the table in the right column beside the chart.
 * "stack": everything in one column (a window under 900px).
 */
export function layoutFor(rowWidth: number): "side" | "stack" {
  return rowWidth > 0 && rowWidth < 900 ? "stack" : "side";
}

/** Where the first-scroll stop lands the main row: under the 48px app bar with 8px to spare. */
export const FIT_TOP = 56;
const FIT_BOTTOM = 12;
/**
 * The width the chart, breakdown and table are laid out at on a desktop before they are
 * scaled as one picture to fit the page.
 */
export const DESIGN_WIDTH = 1600;
/**
 * The most the section is ever laid out wider than DESIGN_WIDTH. On a wide, short window
 * the height holds the scale down and would leave empty strips beside the section, so it
 * is laid out wider to fill them (Drew, 2026-11-04: "expand this page out so it fills out
 * more of the page"). Past this it would start to look stretched, so it stops and centres.
 */
export const MAX_LAYOUT_WIDTH = Math.round(DESIGN_WIDTH * 1.25);
/**
 * How much to scale the section so it fills the page it is on (Drew, 2026-11-04: "when
 * scrolling down it stops at the top … and make the damn charts and boxes fit the page").
 * The smaller of the height fit (between the app bar and the bottom of the window) and
 * the width fit (the room beside the sidebar at the width it is laid out), so it never
 * runs off either edge. It grows as well as shrinks. 1 until both are measured.
 */
export function fitScale(naturalHeight: number, windowHeight: number, availableWidth: number, layoutWidth: number = DESIGN_WIDTH): number {
  if (naturalHeight <= 0 || windowHeight <= 0 || availableWidth <= 0) return 1;
  return Math.min((windowHeight - FIT_TOP - FIT_BOTTOM) / naturalHeight, availableWidth / layoutWidth);
}
/**
 * The width to lay the section out at so that, scaled to fit, it reaches both edges of
 * the page: the narrowest layout at which the width, not the height, is the tighter fit.
 * `heightAt` lays the section out at a width and returns its natural height, which only
 * falls as the width grows (long names wrap less, sometimes a whole row at once), so
 * which fit is tighter flips once and a bisection finds the flip. The narrowest such
 * width gives the biggest scale. Never narrower than DESIGN_WIDTH; if even
 * MAX_LAYOUT_WIDTH is held by the height, it stops there and centres.
 */
export function fillLayoutWidth(heightAt: (width: number) => number, windowHeight: number, availableWidth: number): number {
  const room = windowHeight - FIT_TOP - FIT_BOTTOM;
  if (room <= 0 || availableWidth <= 0) return DESIGN_WIDTH;
  const widthIsTighter = (w: number) => availableWidth / w <= room / heightAt(w);
  if (widthIsTighter(DESIGN_WIDTH)) return DESIGN_WIDTH;
  if (!widthIsTighter(MAX_LAYOUT_WIDTH)) return MAX_LAYOUT_WIDTH;
  let lo = DESIGN_WIDTH;
  let hi = MAX_LAYOUT_WIDTH;
  for (let k = 0; k < 14; k++) {
    const mid = (lo + hi) / 2;
    if (widthIsTighter(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}
/** The chart's drawing width (its viewBox) at a layout width, so it widens without getting taller. */
const CHART_INSET = 26; // the card's two borders and the chart's side padding
const chartInner = (w: number) => ((w - 20) * 1.15) / 2.15 - CHART_INSET;
export function chartViewWidth(layoutWidth: number): number {
  return (640 * chartInner(layoutWidth)) / chartInner(DESIGN_WIDTH);
}
/** The chart's height in the laid-out section: what it is at DESIGN_WIDTH, at every width. */
export const CHART_HEIGHT = (chartInner(DESIGN_WIDTH) * 360) / 640;
